import { Location } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  Query,
} from '@angular/fire/compat/firestore';
import { Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { intersects } from 'radash';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  debounceTime,
  from,
  mergeMap,
  of,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { Group, GroupType } from '../../models/group.model';
import { Lesson } from '../../models/lesson.school.model';
import { SchoolMemberType } from '../../models/member.school.model';
import { UserModel } from '../../models/user.model';
import { SchoolLessonService } from '../../services/lesson.school.service';
import { AlertType } from '../../views/components/dialogs/confirm.dialog.model';
import { DlgConfirmComponent } from '../../views/components/dialogs/dlg-confirm/dlg-confirm.component';
import * as groupActions from '../actions/group.actions';
import * as lessonActions from '../actions/lesson.school.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';
import * as groupSelector from '../selectors/group.selector';

@Injectable()
export class SchoolLessonEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  lessonService: SchoolLessonService = inject(SchoolLessonService);
  toastr: ToastrService = inject(ToastrService);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  location: Location = inject(Location);

  private dialog: MatDialog = inject(MatDialog);

  user: UserModel | null = null;

  private logout$ = new Subject<void>();

  constructor() {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.logout$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  // Effect to fetch Pending Lessons
  getLessons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lessonActions.getLessons),
      debounceTime(500),
      mergeMap(({ groupId, gradeId, createdById, lastRecord }) => {
        // console.log(
        //   `getLessons$: groupId: ${groupId}, gradeId: ${gradeId}, createdById: ${createdById}, academicYear: ${academicYear}, lastRecord: ${lastRecord}`
        // );
        this.logout$.next();
        let query: AngularFirestoreCollection<Lesson> = this.afs
          .collection('groups')
          .doc(groupId)
          .collection('lessons', (ref) => {
            let reference = ref as Query;

            // reference = ref.where('academicYear', '==', academicYear);

            if (gradeId) {
              reference = reference.where('gradeId', '==', gradeId);
            }
            if (createdById) {
              reference = reference.where('createdById', '==', createdById);
            }
            reference = reference.orderBy('modified', 'desc');

            if (lastRecord) reference = reference.startAfter(lastRecord);

            return reference;
          });
        return query.get();
      }),
      mergeMap((lessons) => {
        // console.log(`getLessons$: Lessons: ${lessons.docs.length}`);
        const result: (Lesson | null)[] = lessons.docs.map((doc) => {
          try {
            const lesson = doc.data() as Lesson;
            lesson.id = doc.id;
            // console.log(`Lesson: ${JSON.stringify(lesson)}`);
            return lesson;
          } catch (error) {
            return null;
          }
        });
        const filtered = result.filter((value) => value !== null);
        if (filtered == null) return EMPTY;
        return of(lessonActions.addLessonsToStore({ lessons: filtered }));
      }),
      catchError((error) => {
        // console.error(`Error fetching lessons: ${error}`);
        this.toastr.error(
          'An error occurred. Please try again later.',
          'Error Fetching Lessons',
        );
        return of(lessonActions.getLessonsFailure({ error }));
      }),
    ),
  );

  getLesson$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lessonActions.getLesson),
      debounceTime(500),
      switchMap(({ groupId, lessonId }) => {
        // console.log(`getLesson$: groupId: ${groupId}, lessonId: ${lessonId}`);
        return this.afs
          .collection('groups')
          .doc(groupId)
          .collection('lessons')
          .doc(lessonId)
          .get();
      }),
      switchMap((doc) => {
        if (!doc.exists) {
          // console.log('getLesson$: Lesson not found');
          return of(
            lessonActions.getLessonFailure({
              error: {
                title: 'Lesson not found',
                message:
                  'The lesson could not be fetched. Please try again later.',
              },
            }),
          );
        }
        try {
          const lesson = doc.data() as Lesson;
          lesson.id = doc.id;
          // console.log(`getLesson$: Lesson: ${JSON.stringify(lesson)}`);
          return of(lessonActions.addLessonToStore({ lesson: lesson }));
        } catch (error) {
          return of(
            lessonActions.getLessonFailure({
              error: {
                title: 'Lesson not found',
                message:
                  'The lesson could not be fetched. Please try again later.',
              },
            }),
          );
        }
      }),
      catchError((error) => {
        // console.error(`Error fetching lesson: ${error}`);
        this.toastr.error(
          'An error occurred. Please try again later.',
          'Error Fetching Lesson',
        );
        return of(lessonActions.getLessonsFailure({ error }));
      }),
    ),
  );

  // Effect to create a new lesson
  create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(lessonActions.create),
      debounceTime(500),
      switchMap(({ groupId, lesson }) => {
        const createLesson = {
          ...lesson,
          created: Timestamp.now(),
          modified: Timestamp.now(),
        };

        return from(this.lessonService.create(groupId, createLesson)).pipe(
          switchMap((lessonId) => {
            const localLesson: Lesson = {
              ...lesson,
              id: lessonId,
              created: Timestamp.fromDate(moment(lesson.created).toDate()),
              modified: Timestamp.fromDate(moment(lesson.modified).toDate()),
            };

            // Success notification and navigation
            this.toastr.success(
              'The lesson has been created successfully.',
              'Lesson Created',
            );
            // this.router.navigate(['school', groupId, 'lessons', lesson.id]);
            return of(lessonActions.createSuccess({ lesson: localLesson }));
          }),
          catchError((error) => {
            // console.log(`Error creating lesson: ${error.toString()}`);
            this.toastr.error(
              `An error occurred during Lesson Creation. Please try again later.`,
              'Error Creating Lesson',
            );
            return of(lessonActions.createFailure({ error }));
          }),
        );
      }),
    ),
  );

  // Effect to update a lesson
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(lessonActions.update),
      mergeMap(({ groupId, lessonId, lesson }) => {
        const updateLesson = {
          ...lesson,
          modified: serverTimestamp() as Timestamp,
        };
        return from(
          this.lessonService.update(groupId, lessonId, updateLesson),
        ).pipe(
          mergeMap(() => {
            const localLesson: Lesson = {
              ...lesson,
              id: lessonId,
              modified: Timestamp.fromDate(moment(lesson.modified).toDate()),
            };
            this.onSuccess();
            return of(lessonActions.updateSuccess({ lesson: localLesson }));
          }),
          catchError((error) => {
            // console.error(`Error updating lesson: ${error}`);
            this.toastr.error(
              `An error occurred while updating the lesson details. Please try again later.`,
              'Error Updating Lesson',
            );
            return of(lessonActions.updateFailure({ error }));
          }),
        );
      }),
    ),
  );

  getLessonStats$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select, lessonActions.loadLessonStats),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(([{ groupId }, group]) => {
        this.logout$.next();
        if (group?.groupType !== GroupType.school) return EMPTY;
        if (
          !intersects(group?.memberType as SchoolMemberType[], [
            SchoolMemberType.president,
            SchoolMemberType.principal,
          ])
        ) {
          return EMPTY;
        }
        if (group == undefined) return EMPTY;
        return this.afs
          .collection('groups')
          .doc(group.id)
          .collection<any>('lesson_stats')
          .doc(`${group.id}_${group.academicYear}`)
          .get()
          .pipe(takeUntil(this.logout$))
          .pipe(
            switchMap((snapshot) => {
              if (!snapshot.exists) return EMPTY;
              // console.log(`getGroupStatistics$ :: Stats found.`);
              const record: any = snapshot.data();
              let g: Group = {
                id: group.id,
                lessonStats: record,
                lessonStatsLoaded: true,
              } as Group;

              // console.log(
              //   `getLessonStats$ :: Stats to store: ${JSON.stringify(g)}`
              // );
              return of(groupActions.getLessonStatsDone({ group: g }));
            }),
            catchError((error) => {
              // console.log(`getLessonStats$ :: Error: ${JSON.stringify(error)}`);
              return of(groupActions.getLessonStatsDone({ group: null }));
            }),
          );
      }),
    ),
  );

  // Effect to update a lesson notes
  // updateNotes$: Observable<Action> = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(lessonActions.updateNotes),
  //     switchMap(({ groupId, lessonId, notesData }) => {

  //       return from(this.lessonService.getUploadUrl(groupId, lessonId)).pipe(take(1)).subscribe((response) => {

  //       return from(
  //         this.lessonService.uploadNotes(response.url, notesData)
  //       ).pipe(
  //         switchMap(() => {
  //           const localLesson: Lesson = {
  //             notesUrl: response.url,
  //             modified: Timestamp.fromDate(moment(lesson.modified).toDate()),
  //           };
  //           return of(
  //             lessonActions.updateStatusSuccess({ lesson: localLesson })
  //           );
  //         }),
  //         catchError((error) => {
  //           // console.error(`Error updating lesson: ${error}`);
  //           this.toastr.error(
  //             `An error occurred, '${error}'. Please try again later.`,
  //             'Error Updating Lesson Status'
  //           );
  //           return of(lessonActions.updateStatusFailure({ error }));
  //         })
  //       );
  //     })
  //   )
  // );

  // effect to delete a lesson
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(lessonActions.deleteAction),
      switchMap(({ groupId, id }) =>
        this.lessonService
          .delete(groupId, id)
          .then(() => {
            this.toastr.success(
              'The lesson has been deleted successfully.',
              'Lesson Deleted',
            );
            return lessonActions.deleteSuccess({ id: id });
          })
          .catch((error: any) => {
            // console.error(`Error deleting lesson: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Deleting Lesson',
            );
            return lessonActions.deleteFailure({ error });
          }),
      ),
    ),
  );

  // Get New Lessons who joined in the last 30 days
  //   getLessonStatistics$: Observable<Action> = createEffect(() =>
  //     this.actions$.pipe(
  //       ofType(
  //         lessonActions.getLessonStats,
  //         lessonActions.createSuccess,
  //         lessonActions.updateStatusSuccess
  //       ),
  //       mergeMap((action) => {
  //         let groupId = '';
  //         if (action.type === lessonActions.getLessonStats.type) {
  //           groupId = action.groupId;
  //         } else if (action.type === lessonActions.createSuccess.type) {
  //           groupId = action.lesson.groupId;
  //         } else if (action.type === lessonActions.updateStatusSuccess.type) {
  //           groupId = action.lesson.groupId;
  //         }

  //         return from(this.lessonService.getLessonStats(groupId)).pipe(
  //           switchMap((snapshot) => {
  //             let g: any = {
  //               id: groupId,
  //               newLessons: snapshot?.count ?? '-',
  //             };
  //             return of(lessonActions.getLessonStatsDone({ group: g }));
  //           }),
  //           catchError((error) =>
  //             of(lessonActions.getLessonStatsFailure({ error }))
  //           )
  //         );
  //       })
  //     )
  //   );

  clearLessons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(lessonActions.clear());
      }),
    ),
  );

  onSuccess() {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Success',
      description:
        'Lesson notes have been successfully published.\nKindly wait for the server to update the cache.',
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: AlertType.success,
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
